import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { MailX, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { newsletterService } from '../lib/firebase';

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (email && status === 'idle') {
      handleUnsubscribe();
    }
  }, [email]);

  const handleUnsubscribe = async () => {
    if (!email) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      await newsletterService.unsubscribe(email);
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md bg-white dark:bg-slate-900 border border-line dark:border-slate-800 rounded-3xl p-8 text-center shadow-2xl overflow-hidden relative"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-accent/20"></div>
        
        {status === 'loading' && (
          <div className="py-12">
            <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">Processing request...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="py-12">
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-black mb-4 dark:text-white leading-tight">You're unsubscribed.</h2>
            <p className="text-slate-500 text-sm mb-8 leading-relaxed">
              We've removed <span className="font-bold text-slate-900 dark:text-slate-300">{email}</span> from our list. 
              We're sorry to see you go!
            </p>
            <Link to="/" className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-3 rounded-2xl font-bold text-sm transition-transform hover:scale-105 shadow-xl">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="py-12">
            <div className="w-20 h-20 bg-rose-100 dark:bg-rose-900/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <AlertCircle className="w-10 h-10 text-rose-500" />
            </div>
            <h2 className="text-2xl font-black mb-4 dark:text-white">Something went wrong.</h2>
            <p className="text-slate-500 text-sm mb-8 leading-relaxed">
              We couldn't process your request automatically. You might already be unsubscribed or the link is invalid.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-8 py-3 rounded-2xl font-bold text-sm transition-transform hover:scale-105">
              Return Home
            </Link>
          </div>
        )}

        {!email && status === 'idle' && (
          <div className="py-12">
             <MailX className="w-16 h-16 text-slate-300 mx-auto mb-6" />
             <p className="text-slate-500 text-sm">Please use the link provided in your email to unsubscribe.</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
